import React, { useEffect, useState } from 'react';
import { Building2, Download, Landmark, Play } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/axios';
import { formatApiError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const STATUS = {
  ok: 'Erledigt',
  skipped: 'Übersprungen',
  error: 'Fehlgeschlagen',
};

function istAdmin(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return (user.roles || []).some((rolle) => (rolle.name || rolle) === 'admin');
}

function geld(wert) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(wert || 0);
}

export default function ZentralePage() {
  const { user } = useAuth();
  const admin = istAdmin(user);
  const jahr = new Date().getFullYear();
  const heute = new Date();
  const [schulen, setSchulen] = useState([]);
  const [gewaehlt, setGewaehlt] = useState([]);
  const [monat, setMonat] = useState(
    `${jahr}-${String(heute.getMonth() + 1).padStart(2, '0')}-01`,
  );
  const [lauf, setLauf] = useState(false);
  const [zeilen, setZeilen] = useState([]);

  useEffect(() => {
    if (!admin) return undefined;
    let weg = false;
    api.get('/admin/master/tenants')
      .then((antwort) => {
        if (weg) return;
        const liste = antwort.data || [];
        setSchulen(liste);
        setGewaehlt(liste.map((schule) => schule.id));
      })
      .catch((fehler) => {
        if (!weg) toast.error(formatApiError(fehler, 'Schulen konnten nicht geladen werden'));
      });
    return () => { weg = true; };
  }, [admin]);

  const umschalten = (id) => {
    setGewaehlt((bisher) => (
      bisher.includes(id) ? bisher.filter((wert) => wert !== id) : [...bisher, id]
    ));
  };

  const starten = async (pfad, { dryRun = false, frage } = {}) => {
    if (!gewaehlt.length) {
      toast.error('Bitte mindestens eine Schule auswählen');
      return;
    }
    if (frage && !window.confirm(frage)) return;
    setLauf(true);
    try {
      const koerper = { billing_month: monat, tenant_ids: gewaehlt };
      if (pfad === '/admin/master/billing-run') koerper.dry_run = dryRun;
      const antwort = await api.post(pfad, koerper);
      setZeilen(antwort.data.rows || []);
      toast.success('Fertig');
    } catch (fehler) {
      toast.error(formatApiError(fehler, 'Der Lauf ist fehlgeschlagen'));
    } finally {
      setLauf(false);
    }
  };

  const herunterladen = async (zeile) => {
    try {
      const antwort = await api.get(
        `/admin/master/files/${zeile.tenant_id}/${encodeURIComponent(zeile.reference)}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(antwort.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = zeile.reference;
      link.click();
      URL.revokeObjectURL(url);
    } catch (fehler) {
      toast.error(formatApiError(fehler, 'Die Datei konnte nicht geladen werden'));
    }
  };

  if (!admin) {
    return (
      <Layout>
        <div className="p-8" data-testid="zentrale-denied">
          <h1 className="text-xl font-semibold text-slate-900">Zentrale</h1>
          <p className="mt-2 text-sm text-slate-600">Diese Seite ist den Admins vorbehalten.</p>
        </div>
      </Layout>
    );
  }

  const anzahl = gewaehlt.length;
  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="zentrale-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-slate-700" /> Zentrale
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Rechnungslauf, Lastschriften und Überweisungen für die angehakten Schulen.
            Jede Schule wird mit ihrer eigenen Abrechnung und ihrem eigenen Zahlungsverkehr
            ausgeführt. In der einzelnen Schule bleibt beides wie bisher erreichbar.
          </p>
        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-wrap gap-4">
              {schulen.map((schule) => (
                <label key={schule.id} className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    data-testid={`zentrale-tenant-${schule.slug}`}
                    checked={gewaehlt.includes(schule.id)}
                    onChange={() => umschalten(schule.id)}
                  />
                  {schule.name}
                </label>
              ))}
            </div>
            <label className="block text-sm text-slate-700 max-w-xs">
              Abrechnungsmonat
              <select
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="zentrale-month"
                aria-label="Abrechnungsmonat"
                value={monat}
                onChange={(event) => setMonat(event.target.value)}
              >
                {MONATE.map((name, index) => {
                  const wert = `${jahr}-${String(index + 1).padStart(2, '0')}-01`;
                  return <option key={wert} value={wert}>{name} {jahr}</option>;
                })}
              </select>
            </label>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-5 space-y-3">
              <h2 className="font-semibold text-slate-900">Rechnungslauf</h2>
              <p className="text-sm text-slate-500">Dieselbe Monatsabrechnung wie in der Rechnungslauf-Vorschau.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={lauf}
                  data-testid="zentrale-billing-preview"
                  onClick={() => starten('/admin/master/billing-run', { dryRun: true })}
                >
                  Vorschau
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={lauf}
                  data-testid="zentrale-billing-run"
                  onClick={() => starten('/admin/master/billing-run', {
                    frage: `Rechnungslauf in ${anzahl} Schulen starten? Es werden Rechnungen erzeugt.`,
                  })}
                >
                  <Play className="w-4 h-4 mr-2" /> Rechnungslauf starten
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 space-y-3">
              <h2 className="font-semibold text-slate-900">Lastschriften</h2>
              <p className="text-sm text-slate-500">
                Zum 1. und zum 15. entsteht je Schule eine Lastschriftdatei.
                Die Bank verlangt für die Einreichung eine TAN; das bleibt im Zahlungsverkehr der Schule.
              </p>
              <Button
                disabled={lauf}
                data-testid="zentrale-debits"
                onClick={() => starten('/admin/master/sepa-debits', {
                  frage: `Lastschriften in ${anzahl} Schulen zum 1. und zum 15. anlegen?`,
                })}
              >
                <Landmark className="w-4 h-4 mr-2" /> Lastschriften erstellen
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 space-y-3">
              <h2 className="font-semibold text-slate-900">Überweisungen</h2>
              <p className="text-sm text-slate-500">
                Offene Überweisungen und die Honorar-Datei des Monats, jeweils in der Schule.
              </p>
              <Button
                disabled={lauf}
                data-testid="zentrale-transfers"
                onClick={() => starten('/admin/master/sepa-transfers', {
                  frage: `Überweisungen in ${anzahl} Schulen schreiben?`,
                })}
              >
                <Landmark className="w-4 h-4 mr-2" /> Überweisungen ausführen
              </Button>
            </CardContent>
          </Card>
        </div>

        {zeilen.length > 0 && (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="px-4 py-3 font-medium">Schule</th>
                    <th className="px-4 py-3 font-medium">Stand</th>
                    <th className="px-4 py-3 font-medium">Ergebnis</th>
                    <th className="px-4 py-3 font-medium text-right">Betrag</th>
                    <th className="px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((zeile, index) => (
                    <tr key={`${zeile.tenant_id}-${index}`} className="border-b last:border-0" data-testid="zentrale-result-row">
                      <td className="px-4 py-3 font-medium text-slate-900">{zeile.tenant_name}</td>
                      <td className="px-4 py-3">{STATUS[zeile.status] || zeile.status}</td>
                      <td className="px-4 py-3 text-slate-600">{zeile.message}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{geld(zeile.amount)}</td>
                      <td className="px-4 py-3 text-right">
                        {zeile.reference ? (
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`zentrale-download-${zeile.reference}`}
                            onClick={() => herunterladen(zeile)}
                          >
                            <Download className="w-4 h-4 mr-1" /> Datei
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
